import React from 'react';
import {
  ButtonTertiary,
  cssColorValue,
  desktopBallad,
  IconChevronRight,
  PopoverNavigationItem,
  semanticColors,
  spacer12,
  spacer16,
  spacer32,
  spacer4,
  spacer8,
} from '@spotify-internal/encore-web/core';
import styled from 'styled-components';

import { MastheadLink } from 'lib/sanity/types';
import { urlForImage } from 'utils/sanity';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';

const IconLeadingWrapper = styled.span`
  background-color: ${cssColorValue(semanticColors.backgroundTintedBase)};
  border-radius: ${spacer4};
  display: inline-flex;
  padding: ${spacer8};
`;

const IconLeadingImage = styled.img<{
  imgUrl: string;
}>`
  background-color: ${cssColorValue(semanticColors.textSubdued)};
  mask: ${({ imgUrl }) => `url(${imgUrl}) no-repeat center`};
  height: ${spacer16};
  width: ${spacer16};
`;

const StyledPopoverNavigationLinkText = styled.span<{
  iconTrailing?: boolean;
  iconLeading: boolean;
  level: number;
  isDisabled: boolean;
}>`
  & {
    color: ${({ isDisabled }) =>
      isDisabled ? cssColorValue(semanticColors.textSubdued) : cssColorValue(semanticColors.textBase)};
    flex: 1;
    margin-inline-start: ${({ level, iconLeading }) => (!iconLeading && level === 2 ? spacer32 : 0)};
    text-align: start;
  }
`;

const StyledPopoverNavigationLink = styled(ButtonTertiary)<{
  level: number;
}>`
  color: ${cssColorValue(semanticColors.textSubdued)};
  justify-content: flex-start;
  height: ${spacer32};
  padding-block: ${spacer8};
  padding-inline: 0;
  gap: ${spacer12};
  width: 100%;
  ${desktopBallad};

  &:active,
  &:hover,
  &:focus {
    transform: none;
  }

  &:hover {
    color: ${cssColorValue(semanticColors.textBrightAccent)};

    & ${StyledPopoverNavigationLinkText} {
      color: ${cssColorValue(semanticColors.textBrightAccent)};
    }

    & ${IconLeadingImage} {
      background-color: ${cssColorValue(semanticColors.textBrightAccent)};
    }
  }

  &:active {
    color: ${cssColorValue(semanticColors.textBase)};
    opacity: 1;
  }

  & > * {
    margin: 0;
  }

  &::after {
    bottom: 0;
    inline-size: ${({ level, iconLeading }) => (!iconLeading && level === 2 ? `calc(100% - ${spacer32})` : '100%')};
    margin-inline-start: ${({ level, iconLeading }) => (!iconLeading && level === 2 ? spacer32 : 0)};
  }
`;

const StyledPopoverNavigationItem = styled(PopoverNavigationItem)<{
  isDisabled: boolean;
}>`
  pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'auto')};
  opacity: ${({ isDisabled }) => (isDisabled ? '0.3' : '1')};
`;

interface Props extends MastheadLink {
  nestingLevel: number;
  isDisabled?: boolean;
  onClick?: () => void;
}

export const ProfileLinkItem = ({ target, title, href, image, nestingLevel, isDisabled = false, onClick }: Props) => {
  const {
    verticalConfig: { verticalName },
  } = useServerSideConstants();
  const iconLeadingSrc = image && urlForImage({ source: image.asset, maxWidth: 16, vertical: verticalName });

  return (
    <StyledPopoverNavigationItem isDisabled={isDisabled} onClick={onClick}>
      <StyledPopoverNavigationLink
        target={target}
        href={href}
        iconLeading={
          iconLeadingSrc
            ? () => (
                <IconLeadingWrapper>
                  <IconLeadingImage imgUrl={iconLeadingSrc} alt="" />
                </IconLeadingWrapper>
              )
            : undefined
        }
        level={nestingLevel}
        onClick={onClick}
        iconTrailing={() => <IconChevronRight size="small" />}
      >
        <StyledPopoverNavigationLinkText
          iconTrailing // could be optional later
          iconLeading={!!iconLeadingSrc}
          level={nestingLevel}
          isDisabled={isDisabled}
        >
          {title}
        </StyledPopoverNavigationLinkText>
      </StyledPopoverNavigationLink>
    </StyledPopoverNavigationItem>
  );
};
